import { AppStateModel } from './app.model';
import { Injectable, inject } from '@angular/core';
import { Action, NgxsOnInit, State, StateContext, Store } from '@ngxs/store';
import {
  AbstractSetUserState,
  ClearAppState,
  CollapseSideNav,
  NavigateToDashboardFromLogin,
  NavigateToDashboardFromSignUpComplete,
  OpenSideNav,
  ReloadAppStateFromInit,
  ReloadAppStateFromLogin,
  SetAppStateFromAccountLockedGuard,
  SetAppStateFromAlreadyVerifiedPhoneGuard,
  SetAppStateFromBusinessCertificateGuard,
  SetAppStateFromKycCompleted,
  SetAppStateFromSignUpPasswordSet,
  SetAppStateFromSubscriptionCardCreated,
  SetAppStateFromTermsAcceptedGuard,
  SetAppStateFromVerifyPhoneGuard,
  SetBusinessStateFromPaymentComplete,
  SetBusinessStateFromReloadAppState,
  SetSessionExpired,
  SetUserStateFromNavigateToDashboard,
  SetUserStateFromPhoneVerified,
  SetUserStateFromProfileLoad,
  SetUserStateFromReloadAppState,
  ToggleSideNav,
  UpdatePageTitle,
} from './app.actions';
import { concatMap, mergeMap, Observable, shareReplay, tap } from 'rxjs';
import { UserProfile } from '../../_interfaces/user.interface';
import { UserService } from '../../_services/user-service/user.service';
import { AuthState, JwtHelper, Logout } from '@bo/ng-auth';
import { SetTokens } from '../auth/auth.actions';
import { BusinessApiService } from '../../_services/business-service/business-api.service';
import { AppSelectors } from './app.selectors';
import { HttpErrorResponse } from '@angular/common/http';
import { BusinessProfile } from '../../_interfaces/business.interface';
import {
  ClearAccountingState,
  SetAccountingProviderStateFromAccountingConnection,
} from '../accounting/accounting.actions';
import { ToastService } from '../../_services/toast-service/toast.service';
import { ClearIDVerificationState, StopPollingIDVerificationStatus } from '../id-verification/id-verification.actions';
import { ClearSubscriptionState, SetSubscriptionStateFromReloadAppState } from '../subscription/subscription.actions';
import { OffcanvasService } from '../../_services/offcanvas-service/offcanvas.service';

@State<AppStateModel>({
  name: 'app',
  defaults: {
    sideNavOpen: true,
    pageTitle: 'Default Title',
    accountLocked: undefined,
    sessionExpired: false,
    businessDetails: {} as BusinessProfile,
  },
})
@Injectable()
export class AppState implements NgxsOnInit {
  readonly businessApiService = inject(BusinessApiService);
  readonly userService = inject(UserService);
  readonly store = inject(Store);
  readonly toastService = inject(ToastService);
  readonly offCanvasService = inject(OffcanvasService);

  private userProfile$: Observable<UserProfile> | undefined;
  private businessProfile$: Observable<BusinessProfile> | undefined;
  readonly jwtHelper = new JwtHelper();
  private toasted = false;

  ngxsOnInit({ dispatch }: StateContext<AppStateModel>) {
    const token = this.store.selectSnapshot(AuthState.token);
    if (token && this.jwtHelper.isTokenValid(token)) {
      dispatch(new ReloadAppStateFromInit());
    }
  }

  @Action(UpdatePageTitle)
  setPageTitle(ctx: StateContext<AppStateModel>, action: UpdatePageTitle) {
    ctx.patchState({ pageTitle: action.title });
  }

  @Action(ToggleSideNav)
  toggleSideNav({ getState, patchState }: StateContext<AppStateModel>) {
    patchState({ sideNavOpen: !getState().sideNavOpen });
  }

  @Action(CollapseSideNav)
  collapseSideNav({ patchState }: StateContext<AppStateModel>) {
    patchState({ sideNavOpen: false });
  }

  @Action(OpenSideNav)
  openSideNav({ patchState }: StateContext<AppStateModel>) {
    patchState({ sideNavOpen: true });
  }

  @Action(SetSessionExpired)
  SetSessionExpired(ctx: StateContext<AppStateModel>, action: SetSessionExpired) {
    ctx.patchState({ sessionExpired: action.value });
  }

  @Action([ClearAppState])
  clearAppState({ patchState }: StateContext<AppStateModel>) {
    this.userProfile$ = undefined;
    this.businessProfile$ = undefined;
    patchState({
      userDetails: undefined,
      businessDetails: {} as BusinessProfile,
      accountLocked: undefined,
      sessionExpired: false,
    });
    // set side nav value to what it was, otherwise creates "After content checked" error.
    this.store.select(AppSelectors.sideNavOpen).pipe(
      tap((sidenav) => {
        patchState({
          sideNavOpen: sidenav,
        });
      }),
    );
    this.offCanvasService.close();
    this.store.dispatch(new StopPollingIDVerificationStatus());
    this.store.dispatch(new ClearAccountingState());
    this.store.dispatch(new ClearIDVerificationState());
    this.store.dispatch(new ClearSubscriptionState());
  }

  @Action(SetAppStateFromSignUpPasswordSet)
  setStateFromSignupSuccess({ dispatch }: StateContext<AppStateModel>, action: SetAppStateFromSignUpPasswordSet) {
    dispatch(new SetTokens(action.tokens)).subscribe({
      next: () => dispatch(new SetUserStateFromReloadAppState()),
    });
  }

  @Action([NavigateToDashboardFromLogin, NavigateToDashboardFromSignUpComplete])
  navigateToDashboardFromLogin({ dispatch }: StateContext<AppStateModel>) {
    // Load user state, business state, and accounting state
    // Don't wait for userDetails to exist first - just dispatch the actions
    return dispatch(new SetUserStateFromNavigateToDashboard()).pipe(
      mergeMap(() => dispatch(new SetBusinessStateFromReloadAppState())),
      mergeMap(() => dispatch(new SetAccountingProviderStateFromAccountingConnection())),
    );
  }

  @Action([
    SetUserStateFromNavigateToDashboard,
    SetUserStateFromPhoneVerified,
    SetUserStateFromReloadAppState,
    SetUserStateFromProfileLoad,
    SetAppStateFromVerifyPhoneGuard,
    SetAppStateFromAlreadyVerifiedPhoneGuard,
    SetAppStateFromBusinessCertificateGuard,
    SetAppStateFromTermsAcceptedGuard,
    SetAppStateFromKycCompleted,
    SetAppStateFromSubscriptionCardCreated,
  ])
  setUserProfile({ patchState, dispatch }: StateContext<AppStateModel>, { userProfile }: AbstractSetUserState) {
    function patchUser(accountDetails: UserProfile) {
      return patchState({ userDetails: accountDetails });
    }

    if (userProfile) {
      patchUser(userProfile);
    }

    this.userProfile$ = this.userService.getUserDetails().pipe(
      tap({
        next: (profileData) => {
          patchUser(profileData);
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 401) {
            dispatch(new SetSessionExpired(true));
          }
        },
      }),
      shareReplay(1),
    );
    return this.userProfile$;
  }

  @Action([
    SetBusinessStateFromReloadAppState,
    SetBusinessStateFromPaymentComplete,
    SetAppStateFromVerifyPhoneGuard,
    SetAppStateFromAlreadyVerifiedPhoneGuard,
    SetAppStateFromBusinessCertificateGuard,
    SetAppStateFromAccountLockedGuard,
  ])
  setBusinessDetails({ patchState, dispatch }: StateContext<AppStateModel>) {
    this.businessProfile$ = this.businessApiService.getBusinessProfile().pipe(
      tap({
        next: (data: BusinessProfile) => {
          patchState({ businessDetails: data, accountLocked: false });
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 401) {
            dispatch(new SetSessionExpired(true));
          }
          if (err.status === 402) {
            patchState({ accountLocked: true });
          }
          if (err.status === 403) {
            if (!this.toasted) {
              this.toastService.show('You do not have administrator permission.', 'iconoir-xmark');
              this.toasted = true;
            }
            dispatch(new ClearAppState());
            dispatch(new Logout());
          }
        },
      }),
      shareReplay(1),
    );
    return this.businessProfile$;
  }

  @Action([ReloadAppStateFromInit, ReloadAppStateFromLogin])
  reloadAppState({ dispatch, patchState }: StateContext<AppStateModel>) {
    // Reset session expired first
    patchState({ sessionExpired: false });

    // IMPORTANT: SetSubscriptionStateFromReloadAppState MUST complete first
    return dispatch(new SetSubscriptionStateFromReloadAppState()).pipe(
      concatMap(() => dispatch(new SetUserStateFromReloadAppState())),
      concatMap(() => dispatch(new SetBusinessStateFromReloadAppState())),
      concatMap(() => dispatch(new SetAccountingProviderStateFromAccountingConnection())),
    );
  }
}
